import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import { TerraButton, GhostButton } from "@/components/Buttons";
import ReviewStrip from "@/components/ReviewStrip";
import { IMG, SITE } from "@/constants/site";

export default function SupperClub() {
  const bandRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bandRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div>
      <Seo
        title="The Sita Supper Club — Monthly Ticketed Dinners in Jersey City"
        description="A monthly supper club in Jersey City. Eight to ten guests, a new menu every time, everything from scratch. Any allergy accommodated. Come ready to eat something you have never had before."
      />

      {/* ---------- Section 1: the monthly club ---------- */}
      <section data-testid="supper-club-intro" className="mx-auto max-w-7xl px-6 pb-20 pt-14 md:px-10 md:pb-28 md:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline>Monthly · Jersey City</Overline>
              <h1 className="mt-4 font-display text-5xl font-light leading-[1.05] text-forest md:text-6xl">
                The Sita<br />Supper Club
              </h1>
              <GoldRule className="mt-8" />
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="mt-8 space-y-5 text-[1.05rem] leading-relaxed text-ink/85">
                <p>Our supper club is a monthly dining experience hosted in Jersey City.</p>
                <p>
                  Every month, a new menu drops, built around a different cuisine, a different feeling, a different story
                  worth telling through food. We make everything from scratch, and we accommodate any allergy or
                  restriction.
                </p>
                <p className="font-display text-2xl italic text-forest">We keep the table small.</p>
                <p>
                  Eight to ten guests, so you get to be present, to network, to meet new people, to find yourself in a
                  real conversation with someone you did not know when you walked in.
                </p>
                <p>
                  At Sita, you can come alone or come with friends. But come ready to eat something you have never had
                  before, and to leave with something you did not expect.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-10">
                <TerraButton href={SITE.stanStoreUrl} external testid="supper-club-reserve-btn">
                  Get your seat <span aria-hidden="true">→</span>
                </TerraButton>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="overflow-hidden rounded-3xl">
              <img
                src={IMG.supperClubHero}
                alt="Tonight's menu card resting on a Sita supper club table"
                className="aspect-[4/5] w-full object-cover md:aspect-[5/5]"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- Around the table ---------- */}
      <section data-testid="supper-club-strip" className="mx-auto max-w-7xl px-6 pb-8 md:px-10 md:pb-10">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {[IMG.supperClubCocktails, IMG.supperClubCooking, IMG.supperClubPlate].map((src, i) => (
            <FadeUp key={src} delay={i * 0.12}>
              <div className="group overflow-hidden rounded-3xl" data-testid={`supper-club-moment-${i + 1}`}>
                <img
                  src={src}
                  alt="Moments around a Sita supper club table"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- The room holds people ---------- */}
      <section data-testid="supper-club-mingle" className="mx-auto max-w-7xl px-6 pb-20 md:px-10 md:pb-28">
        <div className="grid gap-4 md:grid-cols-2 md:gap-6">
          {[IMG.supperClubMingle1, IMG.supperClubMingle2].map((src, i) => (
            <FadeUp key={src} delay={i * 0.12}>
              <div className="group overflow-hidden rounded-3xl" data-testid={`supper-club-mingle-${i + 1}`}>
                <img
                  src={src}
                  alt="Guests in conversation in the Sita space"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- Section 2: seats & how it works ---------- */}
      <section data-testid="supper-club-pricing" className="bg-deepforest text-cream">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <FadeUp>
                <Overline light>Pricing & payment</Overline>
                <h2 className="mt-4 font-display text-4xl font-light md:text-5xl">Seats &amp; how it works</h2>
                <GoldRule className="mt-8" />
              </FadeUp>
            </div>
            <div className="lg:col-span-7">
              <FadeUp delay={0.1}>
                <div className="space-y-5 leading-relaxed text-cream/85">
                  <p>
                    Each seat covers the full multi-course experience from first pour to last bite. The menu posts at the
                    start of each month. Seats are limited to eight to ten, booked and paid through our ticket page, and
                    once a seat is reserved it is yours.
                  </p>
                  <p>
                    Want a night of your own? Private supper club events are built around your group and your date. We
                    send a custom proposal, and a {SITE.depositAmount} deposit holds the night, with the balance due{" "}
                    {SITE.balanceDue} before. (See Private Events below.)
                  </p>
                </div>
                <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                  <TerraButton href={SITE.stanStoreUrl} external testid="supper-club-reserve-btn-pricing">
                    Get your seat <span aria-hidden="true">→</span>
                  </TerraButton>
                  <GhostButton to="/inquiries" dark testid="supper-club-private-night-btn">
                    Inquire about a private night
                  </GhostButton>
                </div>
                <div className="mt-12 overflow-hidden rounded-3xl">
                  <img
                    src={IMG.supperClubRoom}
                    alt="Eight guests around the table at a Sita supper club"
                    data-testid="supper-club-room-photo"
                    className="aspect-[16/9] w-full object-cover"
                  />
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* full-bleed parallax band — the doorway into the set table */}
      <section ref={bandRef} className="relative h-[46vh] overflow-hidden md:h-[60vh]" aria-hidden="true">
        <motion.img src={IMG.supperClubHallway} alt="" style={{ y }} className="absolute inset-0 h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-deepforest/25" />
      </section>

      <ReviewStrip />
    </div>
  );
}
